import { clsx, type ClassValue } from "clsx";

export function cn(...inputs: ClassValue[]) {
    return clsx(inputs);
}

// iOS-only: prevent page zoom when focusing inputs by temporarily setting maximum-scale=1
const isIOS = (() => {
    try {
        if (typeof navigator === 'undefined') return false;
        return /iPhone|iPad|iPod/i.test(navigator.userAgent);
    } catch { return false; }
})();

// Detect if the widget is running inside an iframe (e.g. ChatbotIframeEmbed).
// In that case we cannot touch the parent's viewport directly — we postMessage instead.
const isInIframe = (() => {
    try {
        return typeof window !== 'undefined' && window !== window.parent;
    } catch {
        return true; // cross-origin parent access throws → definitely in an iframe
    }
})();

let originalViewport: string | null = null;

function setViewportNoZoom() {
    const meta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
    let viewport = meta;
    if (!viewport) {
        viewport = document.createElement('meta');
        viewport.setAttribute('name', 'viewport');
        document.head.appendChild(viewport);
    }
    if (originalViewport === null) {
        originalViewport = viewport.getAttribute('content');
    }
    const base = originalViewport || 'width=device-width, initial-scale=1';
    const next = base.includes('maximum-scale')
        ? base.replace(/maximum-scale\s*=\s*[^,]+/, 'maximum-scale=1')
        : `${base}, maximum-scale=1`;
    viewport.setAttribute('content', next);
}

function restoreViewportDirect() {
    const meta = document.querySelector('meta[name="viewport"]') as HTMLMetaElement | null;
    if (!meta) return;
    if (originalViewport && originalViewport.trim().length > 0) {
        meta.setAttribute('content', originalViewport);
    } else {
        meta.setAttribute('content', 'width=device-width, initial-scale=1');
    }
}

export function applyNoZoomViewport() {
    if (!isIOS) return;
    if (isInIframe) {
        // Ask the parent page to lock its viewport (the one Safari actually reads)
        try { window.parent.postMessage({ type: 'zentri:input-focus' }, '*'); } catch { /* cross-origin blocked */ }
        return;
    }
    if (typeof document === 'undefined') return;
    setViewportNoZoom();
}

export function restoreViewport() {
    if (!isIOS) return;
    if (isInIframe) {
        try { window.parent.postMessage({ type: 'zentri:input-blur' }, '*'); } catch { /* cross-origin blocked */ }
        return;
    }
    if (typeof document === 'undefined') return;
    restoreViewportDirect();
}

/** Tell the parent page (ChatbotIframeEmbed) to resize the iframe so it does not block page clicks. */
export function notifyParentChatOpenState(isOpen: boolean): void {
    if (!isInIframe) return;
    const type = isOpen ? 'zentri:chat-open' : 'zentri:chat-close';
    try {
        window.parent.postMessage({ type }, '*');
    } catch {
        // cross-origin blocked
    }
}

// Escape HTML for safe insertion into text content
function escapeHtml(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

// Normalize URL: add https:// if no protocol
function normalizeUrl(url: string): string {
    const trimmed = url.trim();
    if (!trimmed) return '#';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
}

// Text formatting: [label](url) → link, *text* → bold
export function formatTextWithBold(text: string): string {
    let out = text;
    // 1. Replace [label](url) with labelled links (parse before bold so link text can contain asterisks)
    out = out.replace(/\[([^\]]*)\]\(([^)]*)\)/g, (_, label: string, url: string) => {
        const href = normalizeUrl(url);
        const safeLabel = escapeHtml(label);
        const safeHref = escapeHtml(href);
        return `<a href="${safeHref}" class="zentri-message-link" target="_blank" rel="noopener noreferrer">${safeLabel}</a>`;
    });
    // 2. Replace *text* with <strong>text</strong> for bold formatting
    out = out.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');
    // 3. Convert newlines to <br> tags (double newline = paragraph gap via two <br>s)
    out = out.replace(/\r\n/g, '\n');
    out = out.replace(/\n\n/g, '<br><br>');
    out = out.replace(/\n/g, '<br>');
    return out;
}

// Parse text blocks and return HTML
export function parseFormattedText(text: string): { __html: string } {
    return { __html: formatTextWithBold(text) };
}

// LocalStorage helpers with TTL and safe JSON handling
export const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

type StoredWithTtl<T> = {
    data: T;
    expiresAt: number;
    lastUpdatedAt?: number;
};

export function setJsonWithTTL<T>(key: string, value: T, ttlMs: number = DEFAULT_TTL_MS): void {
    try {
        if (typeof window === 'undefined' || !('localStorage' in window)) return;
        const expiresAt = Date.now() + Math.max(0, ttlMs || 0);
        const payload: StoredWithTtl<T> = { data: value, expiresAt, lastUpdatedAt: Date.now() };
        window.localStorage.setItem(key, JSON.stringify(payload));
    } catch {
        // ignore
    }
}

export function getJsonWithTTL<T>(key: string): T | null {
    try {
        if (typeof window === 'undefined' || !('localStorage' in window)) return null;
        const raw = window.localStorage.getItem(key);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as StoredWithTtl<T>;
        if (parsed && typeof parsed.expiresAt === 'number') {
            if (Date.now() > parsed.expiresAt) {
                // expired - clean up
                try { window.localStorage.removeItem(key); } catch { }
                return null;
            }
        }
        return (parsed && (parsed as any).data) ?? null;
    } catch {
        return null;
    }
}

export function removeItem(key: string): void {
    try {
        if (typeof window === 'undefined' || !('localStorage' in window)) return;
        window.localStorage.removeItem(key);
    } catch {
        // ignore
    }
}

export function clearChatbotState(botId: string): void {
    try {
        if (typeof window === 'undefined' || !('localStorage' in window)) return;
        const keys = [
            `chatbot:${botId}:state`,
            `chatbot:${botId}:sessionId`,
            `chatbot:${botId}:ui`
        ];
        keys.forEach(key => window.localStorage.removeItem(key));
    } catch {
        // ignore
    }
}

type GtagFn = (...args: any[]) => void;

/** How long an event waits for the site's GA4 tag to load before it is sent anyway. */
const GA4_WAIT_MS = 10000;
const GA4_POLL_MS = 500;

/**
 * The window the site's Google tags live on. The widget normally runs on the
 * page itself; the parent branch covers a same-origin iframe embed. Reading a
 * cross-origin parent throws, which is why it is guarded.
 */
function getAnalyticsWindow(): any {
    try {
        const own = window as any;
        if (own.gtag || own.dataLayer || own.google_tag_manager) return own;
        if (window.parent && window.parent !== window) {
            const parent = window.parent as any;
            if (parent.gtag || parent.dataLayer || parent.google_tag_manager) return parent;
        }
    } catch {
        // Cross-origin parent. Nothing to reach.
    }
    return window;
}

/**
 * The page's gtag, or an equivalent.
 *
 * Sites that install GA4 through Google Tag Manager have a `dataLayer` but often
 * no `window.gtag` function at all. gtag() is only `dataLayer.push(arguments)`,
 * and the Google tag GTM loads reads commands from the same dataLayer, so the
 * stand-in reaches it just the same. It must push the `arguments` object itself
 * — the Google tag ignores a plain array.
 */
export function getGtag(win: any = getAnalyticsWindow()): GtagFn | undefined {
    if (typeof win.gtag === 'function') return win.gtag;
    if (Array.isArray(win.dataLayer)) {
        const dataLayer = win.dataLayer;
        return function () {
            // eslint-disable-next-line prefer-rest-params
            dataLayer.push(arguments);
        };
    }
    return undefined;
}

/**
 * Every GA4 property the page has loaded, however it was installed.
 *
 * Both gtag.js and GTM's Google tag register each measurement ID they load as a
 * key of `window.google_tag_manager`. Naming them in `send_to` is what makes an
 * event reach a GA4 tag that lives inside GTM: without it, an event only goes to
 * the destinations the on-page gtag configured itself — on many sites that is
 * just a Google Ads (AW-) tag or a dead Universal Analytics (UA-) one.
 */
function getGa4MeasurementIds(win: any): string[] {
    try {
        const gtm = win.google_tag_manager;
        if (!gtm || typeof gtm !== 'object') return [];
        return Object.keys(gtm).filter(key => /^G-[A-Z0-9]+$/.test(key));
    } catch {
        return [];
    }
}

/**
 * Send one of our GA4 events.
 *
 * Every event carries `bot_id` as a parameter, not just as `event_label`. A GA4
 * property belongs to a website, and a website can host more than one of our
 * bots; without a parameter the property can only report them added together.
 * The CRM's own GA4 report filters on this, when the property has registered
 * `bot_id` as a custom dimension — see integrations.service.js GA4_BOT_DIMENSION.
 *
 * `event_label` is kept because existing properties may already be reporting on
 * it, and removing it would break whatever they built on top.
 *
 * The event is addressed to every GA4 property on the page (see
 * getGa4MeasurementIds). A site's GA4 tag can load after us — GTM, consent
 * banners and script-delay plugins all hold it back — so the event waits up to
 * GA4_WAIT_MS for one to appear, then goes out unaddressed as before.
 */
export function sendGtagEvent(eventName: string, botId: string, params: Record<string, any> = {}): void {
    const payload: Record<string, any> = {
        event_category: 'Chatbot',
        event_label: botId,
        bot_id: botId,
        ...params
    };
    const startedAt = Date.now();

    const attempt = () => {
        try {
            const win = getAnalyticsWindow();
            const ids = getGa4MeasurementIds(win);
            if (ids.length === 0 && Date.now() - startedAt < GA4_WAIT_MS) {
                setTimeout(attempt, GA4_POLL_MS);
                return;
            }

            const gtag = getGtag(win);
            if (!gtag) {
                console.log(`[CHATBOT-WIDGET-V2] No gtag or dataLayer on page; ${eventName} not sent`);
                return;
            }
            gtag('event', eventName, ids.length ? { ...payload, send_to: ids } : payload);
            console.log(`[CHATBOT-WIDGET-V2] Sent ${eventName} to ${ids.length ? ids.join(', ') : 'default gtag destinations'}`);
        } catch {
            // Analytics must never take the widget down with it.
        }
    };
    attempt();
}

/**
 * The session identifier the analytics funnel is joined on.
 *
 * All four events have to agree on it or the funnel cannot be read: an open, an
 * engagement and a submission from one conversation must share an id, and the
 * backend's duplicate suppression matches on it too. It used to be minted
 * independently in three places, so the submission never lined up with the open
 * it came from.
 *
 * Stored as `<id>` followed by the stages already recorded, e.g.
 * `session-1712345678901:opened:engaged`, which keeps the shape older tabs wrote.
 */
const TRACK_SESSION_KEY = (botId: string) => `chatbot:${botId}:track-session`;

function readTrackSession(botId: string): { id: string; stages: string[] } | null {
    try {
        const raw = window.sessionStorage.getItem(TRACK_SESSION_KEY(botId));
        if (!raw) return null;
        const [id, ...stages] = raw.split(':');
        return id ? { id, stages } : null;
    } catch {
        return null;
    }
}

export function getTrackingSessionId(botId: string): string {
    const existing = readTrackSession(botId);
    if (existing) return existing.id;

    const id = 'session-' + Date.now();
    try {
        window.sessionStorage.setItem(TRACK_SESSION_KEY(botId), id);
    } catch {
        // Private mode, or storage full. The id still holds for this page load.
    }
    return id;
}

export function hasTrackedStage(botId: string, stage: 'opened' | 'engaged'): boolean {
    const existing = readTrackSession(botId);
    if (!existing) return false;
    // 'enagaged' was the spelling this marker was written with for a long time.
    // Reading both means a tab open across the fix does not report its
    // engagement a second time.
    const spellings = stage === 'engaged' ? ['engaged', 'enagaged'] : [stage];
    return existing.stages.some((s) => spellings.includes(s));
}

export function markTrackedStage(botId: string, stage: 'opened' | 'engaged'): void {
    try {
        if (hasTrackedStage(botId, stage)) return;
        const id = getTrackingSessionId(botId);
        const existing = readTrackSession(botId);
        const stages = existing ? existing.stages : [];
        window.sessionStorage.setItem(TRACK_SESSION_KEY(botId), [id, ...stages, stage].join(':'));
    } catch {
        // ignore
    }
}

/** Ends the tracking session, so the next conversation is counted as its own. */
export function resetTrackingSession(botId: string): void {
    try {
        window.sessionStorage.removeItem(TRACK_SESSION_KEY(botId));
    } catch {
        // ignore
    }
}

// Analytics tracking helper
export async function trackAnalytics(
    botId: string,
    eventType: 'chatbot_opened' | 'user_engaged' | 'lead_submitted' | 'action_button_click',
    sessionId: string,
    apiBaseUrl: string = 'http://localhost:5002',
    value?: string
): Promise<void> {
    try {
        const response = await fetch(`${apiBaseUrl}/api/chatbot/analytics`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                botId,
                eventType,
                sessionId,
                value,
                metadata: {
                    widget_version: 'v2',
                    timestamp: new Date().toISOString()
                }
            }),
        });

        if (!response.ok) {
            console.error(`[CHATBOT-WIDGET-V2] Analytics tracking failed: ${response.statusText}`);
        } else {
            console.log(`[CHATBOT-WIDGET-V2] Analytics event tracked: ${eventType}`);
        }
    } catch (error) {
        console.error('[CHATBOT-WIDGET-V2] Error tracking analytics:', error);
    }
}
